import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import "./createDraft.css";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";

export default function CreateDraftPage() {
  const [content, setContent] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [precheck, setPrecheck] = useState<any>(null);
  const [images, setImages] = useState<string[]>([]);
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const token = localStorage.getItem("token");

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const list: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const reader = new FileReader();
      reader.onload = () => {
        list.push(reader.result as string);
        if (list.length === files.length) {
          setImages((prev) => [...prev, ...list]);
        }
      };
      reader.readAsDataURL(files[i]);
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const createDraft = async () => {
    setError(null);
    if (!content.trim()) {
      setError("Content cannot be empty");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
        `${API}/drafts`,
        { content, platform },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setDraftId(res.data.id);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to create draft");
    } finally {
      setLoading(false);
    }
  };

  const runPrePostCheck = async () => {
    if (!draftId) {
      setError("Please save draft first");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
        `${API}/ai/pre-post-check`,
        {
          draft_id: draftId,
          message_content: content,
          images: images,
          keywords: [],
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPrecheck(res.data);
    } catch (e: any) {
      console.log(e);
      setError(e.response?.data?.detail || "Safety check failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="create-draft-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="create-draft-card">
        <h1>Create Draft</h1>
        <p className="subtitle">
          Write, add images and run AI safety before publishing.
        </p>

        {/* TEXT AREA */}
        <textarea
          placeholder="Write your content here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />

        {/* IMAGE UPLOAD */}
        <div className="image-upload-section">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            hidden
            onChange={handleImageUpload}
          />

          <motion.button
            type="button"
            className="upload-btn"
            whileTap={{ scale: 0.96 }}
            whileHover={{ scale: 1.03 }}
            onClick={() => fileInputRef.current?.click()}
          >
            + Add Images
          </motion.button>

          <div className="image-preview-grid">
            <AnimatePresence>
              {images.map((img, idx) => (
                <motion.div
                  key={idx}
                  className="image-preview-box"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <img src={img} alt={`preview-${idx}`} />
                  <button
                    className="remove-image-btn"
                    onClick={() => removeImage(idx)}
                  >
                    ×
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* PLATFORM */}
        <div className="platform-row">
          <label>Platform</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
            <option value="twitter">Twitter</option>
          </select>
        </div>

        {error && <div className="error-box">{error}</div>}

        {/* ACTIONS */}
        <div className="actions-row">
          <motion.button
            className="primary-btn"
            whileTap={{ scale: 0.97 }}
            whileHover={{ scale: 1.02 }}
            onClick={createDraft}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Draft"}
          </motion.button>

          {draftId && (
            <motion.button
              className="secondary-btn"
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: 1.02 }}
              onClick={runPrePostCheck}
              disabled={loading}
            >
              {loading ? "Checking..." : "Run Safety Check"}
            </motion.button>
          )}
        </div>

        {/* AI RESULT */}
        {precheck && (
          <motion.div
            className={`precheck-box ${precheck.verdict?.toLowerCase()}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3>AI Verdict: {precheck.verdict}</h3>
            <p>Risk Score: {precheck.risk_score} / 100</p>

            <ul>
              {precheck.reasons?.map((r: string, i: number) => (
                <li key={i}>{r}</li>
              ))}
            </ul>

            {/* 👉 PROCEED TO AUTO FIX */}
            <motion.button
              className="proceed-autofix-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() =>
                navigate("/auto-fix", {
                  state: {
                    draftId: draftId,
                    imageUrls: images,
                  },
                })
              }
            >
              Proceed to Auto Fix →
            </motion.button>
          </motion.div>
        )}

      </div>
    </motion.div>
  );
}
